import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteServiceAccountToken, type PlainServiceAccountToken } from "@/queries/service-account-token-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    token: PlainServiceAccountToken;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
    onClose: () => void;
};

export const ServiceAccountDeleteTokenModal: FC<Props> = ({ token, serviceAccountId, headers, onClose }) => {
    const { toast } = useToast();
    const deleteToken = useDeleteServiceAccountToken({ headers });

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            try {
                await deleteToken.mutateAsync({
                    serviceAccountId,
                    tokenId: token.id,
                });
                onClose();
                toast({
                    title: "Token was successfully deleted",
                });
            } catch (e) {
                toast({
                    title: "Failed to delete token",
                    description: formatError(e),
                });
            }
        },
        [serviceAccountId, token.id, deleteToken, onClose, toast],
    );

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="service-account-delete-token-modal-content"
                data-track-location={TrackLocations.ServiceAccountDeleteTokenModal}
            >
                <DialogHeader>
                    <DialogTitle>Delete token</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <form
                        id="service-account-delete-token-form"
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-4"
                    >
                        <ErrorMessage
                            error={deleteToken.error}
                            data-testid="error-message-service-account-token-deletion-failed"
                        />
                        <Text className="text-base">Are you sure you want to delete this token?</Text>
                        <div className="rounded-xl border-0.5 border-border-base bg-surface-glass px-6 py-3">
                            <Text className="text-base font-bold">{token.description || "Unnamed token"}</Text>
                        </div>
                    </form>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline" autoFocus={true}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="destructive"
                        form="service-account-delete-token-form"
                        loading={deleteToken.isPending}
                        disabled={deleteToken.isPending}
                        data-testid="service-account-delete-token-submit-button"
                        data-tracking-id="confirm-delete-token-service-account-delete-token-modal"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
